import React, { useState, useEffect, useRef } from "react";
import { format, parse, parseISO, getYear, getMonth, subYears } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Edit Secondary ID Modal
export const EditSecondaryIdModal = ({
  showSecondaryIdEditModal,
  handleSecondaryIdModalClose,
  applicant,
  handleSecondaryIdSaveClick,
  provinceList,
  idTypes,
  finInfo,
}) => {
  const [secondaryIdFormData, setSecondaryIdFormData] = useState({
    idType1: applicant?.member?.idType1 || "",
    placeOfIssuance1: applicant?.member?.placeOfIssuance1 || "",
    issueDate1: applicant?.member?.issueDate1
      ? format(parseISO(applicant.member.issueDate1), "MM-dd-yyyy")
      : "",
    expiryDate1: applicant?.member?.expiryDate1
      ? format(parseISO(applicant.member.expiryDate1), "MM-dd-yyyy")
      : "",
    idDetail1: applicant?.member?.idDetail1 || "",
  });

  const [secondaryIdFormErrors, setSecondaryIdFormErrors] = useState({
    idType1: "",
    placeOfIssuance1: "",
    issueDate1: "",
    expiryDate1: "",
    idDetail1: "",
  });

  const [isIssueDatePickerOpen, setIsIssueDatePickerOpen] = useState(false);
  const [isExpiryDatePickerOpen, setIsExpiryDatePickerOpen] = useState(false);

  const issueDatePickerRef = useRef(null);
  const expiryDatePickerRef = useRef(null);

  useEffect(() => {
    setSecondaryIdFormData({
      idType1: applicant?.member?.idType1 || "",
      placeOfIssuance1: applicant?.member?.placeOfIssuance1 || "",
      issueDate1: applicant?.member?.issueDate1
        ? format(parseISO(applicant.member.issueDate1), "MM-dd-yyyy")
        : "",
      expiryDate1: applicant?.member?.expiryDate1
        ? format(parseISO(applicant.member.expiryDate1), "MM-dd-yyyy")
        : "",
      idDetail1: applicant?.member?.idDetail1 || "",
    });
  }, [applicant]);

  // Close date pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on Save or Cancel buttons
      const isButton = event.target.closest('button[type="button"]');
      if (
        isButton &&
        (isButton.textContent === "SAVE" || isButton.textContent === "CANCEL")
      ) {
        return;
      }

      if (
        issueDatePickerRef.current &&
        !issueDatePickerRef.current.contains(event.target)
      ) {
        setIsIssueDatePickerOpen(false);
      }
      if (
        expiryDatePickerRef.current &&
        !expiryDatePickerRef.current.contains(event.target)
      ) {
        setIsExpiryDatePickerOpen(false);
      }
    };

    if (isIssueDatePickerOpen || isExpiryDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isIssueDatePickerOpen, isExpiryDatePickerOpen]);

  const handleSecondaryIdInputChange = (e) => {
    const { name, value } = e.target;
    setSecondaryIdFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    console.log("handleSave", secondaryIdFormData);
    const errors = {
      idType1: "",
      placeOfIssuance1: "",
      issueDate1: "",
      expiryDate1: "",
      idDetail1: "",
    };

    // Validate ID type
    if (
      !secondaryIdFormData.idType1 ||
      secondaryIdFormData.idType1.trim() === ""
    ) {
      errors.idType1 = "ID type is required";
    }

    // Validate place of issuance
    if (
      !secondaryIdFormData.placeOfIssuance1 ||
      secondaryIdFormData.placeOfIssuance1.trim() === ""
    ) {
      errors.placeOfIssuance1 = "State/Province/Territory is required";
    }

    // Validate issue date
    if (
      !secondaryIdFormData.issueDate1 ||
      secondaryIdFormData.issueDate1.trim() === ""
    ) {
      errors.issueDate1 = "Issue date is required";
    }

    // Validate expiry date
    if (
      !secondaryIdFormData.expiryDate1 ||
      secondaryIdFormData.expiryDate1.trim() === ""
    ) {
      errors.expiryDate1 = "Expiry date is required";
    } else {
      // Check if expiry date is after issue date
      const issueDate = secondaryIdFormData.issueDate1
        ? parse(secondaryIdFormData.issueDate1, "MM-dd-yyyy", new Date())
        : null;
      const expiryDate = secondaryIdFormData.expiryDate1
        ? parse(secondaryIdFormData.expiryDate1, "MM-dd-yyyy", new Date())
        : null;

      if (issueDate && expiryDate && expiryDate <= issueDate) {
        errors.expiryDate1 = "Expiry date must be after issue date";
      } else if (expiryDate && expiryDate <= new Date()) {
        errors.expiryDate1 = "Expiry date must be in the future";
      }
    }

    // Validate license/ID number
    if (
      !secondaryIdFormData.idDetail1 ||
      secondaryIdFormData.idDetail1.trim() === ""
    ) {
      errors.idDetail1 = "License/ID number is required";
    }

    setSecondaryIdFormErrors(errors);

    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (!hasErrors) {
      setIsIssueDatePickerOpen(false);
      setIsExpiryDatePickerOpen(false);
      handleSecondaryIdSaveClick(secondaryIdFormData);
    }
  };

  const handleClose = () => {
    setSecondaryIdFormData({
      idType1: applicant?.member?.idType1 || "",
      placeOfIssuance1: applicant?.member?.placeOfIssuance1 || "",
      issueDate1: applicant?.member?.issueDate1
        ? format(parseISO(applicant.member.issueDate1), "MM-dd-yyyy")
        : "",
      expiryDate1: applicant?.member?.expiryDate1
        ? format(parseISO(applicant.member.expiryDate1), "MM-dd-yyyy")
        : "",
      idDetail1: applicant?.member?.idDetail1 || "",
    });
    setSecondaryIdFormErrors({
      idType1: "",
      placeOfIssuance1: "",
      issueDate1: "",
      expiryDate1: "",
      idDetail1: "",
    });
    setIsIssueDatePickerOpen(false);
    setIsExpiryDatePickerOpen(false);
    handleSecondaryIdModalClose();
  };

  if (!showSecondaryIdEditModal) return null;

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          width: "90%",
          maxWidth: "500px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "18px",
            cursor: "pointer",
            color: "#6c757d",
          }}
        >
          ×
        </button>

        <h4
          style={{
            marginBottom: "24px",
            fontWeight: "600",
            color: "#2c3e50",
            marginRight: "30px",
          }}
        >
          Update Secondary ID
        </h4>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            ID Type:
          </label>
          <select
            name="idType1"
            value={secondaryIdFormData.idType1}
            onChange={handleSecondaryIdInputChange}
            style={{
              width: "100%",
              padding: "12px",
              border: secondaryIdFormErrors.idType1
                ? "1px solid #dc3545"
                : "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "14px",
              outline: "none",
            }}
          >
            <option value="">Select ID Type</option>
            <option key={1} value={1}>
              Drivers License
            </option>
            <option key={2} value={2}>
              Passport
            </option>
          </select>
          {secondaryIdFormErrors.idType1 && (
            <div className="[ error-msg ]">{secondaryIdFormErrors.idType1}</div>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            State/Province/Territory:
          </label>
          <select
            name="placeOfIssuance1"
            value={secondaryIdFormData.placeOfIssuance1}
            onChange={handleSecondaryIdInputChange}
            style={{
              width: "100%",
              padding: "12px",
              border: secondaryIdFormErrors.placeOfIssuance1
                ? "1px solid #dc3545"
                : "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "14px",
              outline: "none",
            }}
          >
            <option value="">Select</option>
            {provinceList &&
              provinceList
                .filter((province) => province.country === finInfo?.idCountry1)
                .map((province) => (
                  <option key={province.code} value={province.code}>
                    {province.description}
                  </option>
                ))}
          </select>
          {secondaryIdFormErrors.placeOfIssuance1 && (
            <div className="[ error-msg ]">
              {secondaryIdFormErrors.placeOfIssuance1}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            Issue Date:
          </label>
          <div ref={issueDatePickerRef}>
            <input
              type="text"
              name="issueDate1"
              value={secondaryIdFormData.issueDate1}
              onChange={handleSecondaryIdInputChange}
              onFocus={() => setIsIssueDatePickerOpen(true)}
              placeholder="MM-DD-YYYY"
              style={{
                width: "100%",
                padding: "12px",
                border: secondaryIdFormErrors.issueDate1
                  ? "1px solid #dc3545"
                  : "1px solid #dee2e6",
                borderRadius: "4px",
                fontSize: "14px",
                outline: "none",
                marginBottom: isIssueDatePickerOpen ? "8px" : "0",
              }}
              readOnly
            />
            {isIssueDatePickerOpen && (
              <DatePicker
                selected={
                  secondaryIdFormData.issueDate1
                    ? parse(
                        secondaryIdFormData.issueDate1,
                        "MM-dd-yyyy",
                        new Date()
                      )
                    : null
                }
                onChange={(date) => {
                  const formattedDate = date ? format(date, "MM-dd-yyyy") : "";
                  handleSecondaryIdInputChange({
                    target: {
                      name: "issueDate1",
                      value: formattedDate,
                    },
                  });
                  setIsIssueDatePickerOpen(false);
                }}
                minDate={subYears(new Date(), 50)}
                maxDate={new Date()}
                inline
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => {
                  const years = Array.from(
                    { length: 50 },
                    (_, i) => getYear(new Date()) - i
                  );
                  const months = [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ];
                  return (
                    <div className="custom-datepicker-header">
                      <div className="custom-datepicker-clear-section">
                        <button
                          type="button"
                          onClick={() => {
                            handleSecondaryIdInputChange({
                              target: {
                                name: "issueDate1",
                                value: "",
                              },
                            });
                            setIsIssueDatePickerOpen(false);
                          }}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #dc3545",
                            background: "#dc3545",
                            color: "white",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                          title="Clear date"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="custom-datepicker-nav-section">
                        <button
                          type="button"
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {"<"}
                        </button>
                        <select
                          value={getYear(date)}
                          onChange={({ target: { value } }) => {
                            changeYear(value);
                            const newDate = new Date(date);
                            newDate.setFullYear(parseInt(value));
                            const formattedDate = format(newDate, "MM-dd-yyyy");
                            handleSecondaryIdInputChange({
                              target: {
                                name: "issueDate1",
                                value: formattedDate,
                              },
                            });
                          }}
                          style={{
                            padding: "4px",
                            border: "1px solid #ccc",
                          }}
                        >
                          {years.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <select
                          value={months[getMonth(date)]}
                          onChange={({ target: { value } }) => {
                            const monthIndex = months.indexOf(value);
                            changeMonth(monthIndex);
                            const newDate = new Date(date);
                            newDate.setMonth(monthIndex);
                            const formattedDate = format(newDate, "MM-dd-yyyy");
                            handleSecondaryIdInputChange({
                              target: {
                                name: "issueDate1",
                                value: formattedDate,
                              },
                            });
                          }}
                          style={{
                            padding: "4px",
                            border: "1px solid #ccc",
                          }}
                        >
                          {months.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {">"}
                        </button>
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </div>
          {secondaryIdFormErrors.issueDate1 && (
            <div className="[ error-msg ]">
              {secondaryIdFormErrors.issueDate1}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            Expiry Date:
          </label>
          <div ref={expiryDatePickerRef}>
            <input
              type="text"
              name="expiryDate1"
              value={secondaryIdFormData.expiryDate1}
              onChange={handleSecondaryIdInputChange}
              onFocus={() => setIsExpiryDatePickerOpen(true)}
              placeholder="MM-DD-YYYY"
              style={{
                width: "100%",
                padding: "12px",
                border: secondaryIdFormErrors.expiryDate1
                  ? "1px solid #dc3545"
                  : "1px solid #dee2e6",
                borderRadius: "4px",
                fontSize: "14px",
                outline: "none",
                marginBottom: isExpiryDatePickerOpen ? "8px" : "0",
              }}
              readOnly
            />
            {isExpiryDatePickerOpen && (
              <DatePicker
                selected={
                  secondaryIdFormData.expiryDate1
                    ? parse(
                        secondaryIdFormData.expiryDate1,
                        "MM-dd-yyyy",
                        new Date()
                      )
                    : null
                }
                onChange={(date) => {
                  const formattedDate = date ? format(date, "MM-dd-yyyy") : "";
                  handleSecondaryIdInputChange({
                    target: {
                      name: "expiryDate1",
                      value: formattedDate,
                    },
                  });
                  setIsExpiryDatePickerOpen(false);
                }}
                minDate={new Date()}
                maxDate={subYears(new Date(), -50)}
                inline
                renderCustomHeader={({
                  date,
                  changeYear,
                  changeMonth,
                  decreaseMonth,
                  increaseMonth,
                  prevMonthButtonDisabled,
                  nextMonthButtonDisabled,
                }) => {
                  const years = Array.from(
                    { length: 50 },
                    (_, i) => getYear(new Date()) + i
                  );
                  const months = [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ];
                  return (
                    <div className="custom-datepicker-header">
                      <div className="custom-datepicker-clear-section">
                        <button
                          type="button"
                          onClick={() => {
                            handleSecondaryIdInputChange({
                              target: {
                                name: "expiryDate1",
                                value: "",
                              },
                            });
                            setIsExpiryDatePickerOpen(false);
                          }}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #dc3545",
                            background: "#dc3545",
                            color: "white",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                          title="Clear date"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="custom-datepicker-nav-section">
                        <button
                          type="button"
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {"<"}
                        </button>
                        <select
                          value={getYear(date)}
                          onChange={({ target: { value } }) => {
                            changeYear(value);
                            const newDate = new Date(date);
                            newDate.setFullYear(parseInt(value));
                            const formattedDate = format(newDate, "MM-dd-yyyy");
                            handleSecondaryIdInputChange({
                              target: {
                                name: "expiryDate1",
                                value: formattedDate,
                              },
                            });
                          }}
                          style={{
                            padding: "4px",
                            border: "1px solid #ccc",
                          }}
                        >
                          {years.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <select
                          value={months[getMonth(date)]}
                          onChange={({ target: { value } }) => {
                            const monthIndex = months.indexOf(value);
                            changeMonth(monthIndex);
                            const newDate = new Date(date);
                            newDate.setMonth(monthIndex);
                            const formattedDate = format(newDate, "MM-dd-yyyy");
                            handleSecondaryIdInputChange({
                              target: {
                                name: "expiryDate1",
                                value: formattedDate,
                              },
                            });
                          }}
                          style={{
                            padding: "4px",
                            border: "1px solid #ccc",
                          }}
                        >
                          {months.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {">"}
                        </button>
                      </div>
                    </div>
                  );
                }}
              />
            )}
          </div>
          {secondaryIdFormErrors.expiryDate1 && (
            <div className="[ error-msg ]">
              {secondaryIdFormErrors.expiryDate1}
            </div>
          )}
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              color: "#495057",
            }}
          >
            License #:
          </label>
          <input
            type="text"
            name="idDetail1"
            value={secondaryIdFormData.idDetail1}
            onChange={handleSecondaryIdInputChange}
            style={{
              width: "100%",
              padding: "12px",
              border: secondaryIdFormErrors.idDetail1
                ? "1px solid #dc3545"
                : "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "14px",
              outline: "none",
            }}
          />
          {secondaryIdFormErrors.idDetail1 && (
            <div className="[ error-msg ]">
              {secondaryIdFormErrors.idDetail1}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: "10px 20px",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              backgroundColor: "white",
              color: "#6c757d",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              backgroundColor: "#007bff",
              color: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
};
